import { useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';
import { trackReveal, ListingType } from '@/lib/tracking';

interface EmailContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentName?: string;
  agencyName?: string;
  agentLogo?: string | null;
  phoneNumber?: string;
  referenceId: string;
  propertyId?: string;
  projectId?: string;
  listingId?: string;
  listingType?: ListingType;
}

export function EmailContactDialog({
  open,
  onOpenChange,
  agentName = 'Property Agent',
  agencyName,
  agentLogo,
  phoneNumber,
  referenceId,
  propertyId,
  projectId,
  listingId,
  listingType,
}: EmailContactDialogProps) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: `I would like to inquire about your property EthioRealEstates - ${referenceId}. Please contact me at your earliest convenience.`,
  });
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await leadApi.create({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim() || undefined,
        property_id: propertyId || undefined,
        project_id: projectId || undefined,
        source: 'email_dialog',
      });
      // Record an "email reveal" once the inquiry actually goes through.
      const finalListingId = listingId || propertyId || projectId;
      const finalType: ListingType | undefined = listingType
        || (propertyId ? 'property' : projectId ? 'project' : undefined);
      if (finalListingId && finalType) {
        trackReveal({ listingType: finalType, listingId: finalListingId, revealType: 'email' });
      }
      toast.success('Your inquiry has been sent!');
      onOpenChange(false);
      setForm({
        name: '',
        email: '',
        phone: '',
        message: `I would like to inquire about your property EthioRealEstates - ${referenceId}. Please contact me at your earliest convenience.`,
      });
    } catch {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-lg font-bold">Contact Agent</DialogTitle>
        </DialogHeader>

        {/* Agent info */}
        <div className="px-5 pb-3 flex flex-col items-center text-center">
          {agentLogo ? (
            <img src={agentLogo} alt={agentName} className="h-14 w-14 rounded-full object-cover border mb-2" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl mb-2">
              {agentName.charAt(0)}
            </div>
          )}
          <div className="font-semibold text-sm">{agentName}</div>
          {agencyName && <div className="text-xs text-muted-foreground">{agencyName}</div>}
          <div className="text-[10px] text-muted-foreground mt-0.5">ID: {referenceId}</div>
        </div>

        {/* Call button */}
        {phoneNumber && (
          <div className="px-5 pb-3">
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2"
              asChild
            >
              <a href={`tel:${phoneNumber}`}>
                <Phone className="h-4 w-4" /> Call
              </a>
            </Button>
          </div>
        )}

        <Separator />

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ec-name" className="text-xs">Name <span className="text-destructive">*</span></Label>
            <Input id="ec-name" required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-email" className="text-xs">Email <span className="text-destructive">*</span></Label>
            <Input id="ec-email" type="email" required placeholder="Your email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-phone" className="text-xs">Phone <span className="text-destructive">*</span></Label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1 border rounded-md px-2 text-sm text-muted-foreground shrink-0">
                🇪🇹 +251
              </div>
              <Input id="ec-phone" type="tel" required placeholder="Phone number" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 text-sm" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-message" className="text-xs">Message <span className="text-destructive">*</span></Label>
            <Textarea id="ec-message" rows={3} required value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="text-sm" />
          </div>

          {/* Role selector */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">I am a:</Label>
            <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
              {[
                { value: 'buyer', label: 'Buyer / Tenant' },
                { value: 'agent', label: 'Agent' },
                { value: 'other', label: 'Other' },
              ].map((opt) => (
                <div key={opt.value} className="flex items-center gap-1.5">
                  <RadioGroupItem value={opt.value} id={`role-${opt.value}`} />
                  <Label htmlFor={`role-${opt.value}`} className="text-xs cursor-pointer">{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Keep informed */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="keep-informed"
              checked={keepInformed}
              onCheckedChange={(c) => setKeepInformed(!!c)}
            />
            <Label htmlFor="keep-informed" className="text-xs cursor-pointer">
              Keep me informed about similar properties.
            </Label>
          </div>

          <Button
            type="submit"
            variant="outline"
            className="w-full h-10 font-bold text-primary border-primary/30 bg-primary/5 hover:bg-primary/10 gap-2"
            disabled={submitting}
          >
            <Mail className="h-4 w-4" />
            {submitting ? 'Sending...' : 'SEND EMAIL'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
